import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, RotateCcw, SkipForward } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ProgressBar } from '../../../../components/ui/ProgressBar';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { TaskTimeChip } from '../../components/TaskTimeChip';
import { RouteResult } from './RouteResult';
import { RouteShortSheet } from './RouteShortSheet';
import { RouteStopCard } from './RouteStopCard';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW } from '../../../../features/operator/operatorConstants';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import { selectEnabledFlows } from '../../../../features/operator/operatorUtils';
import {
  ROUTE_WRITES,
  fetchPickingRoute,
  retryLastRouteWrite,
  scanRouteCode,
  submitTake,
  takeAtStop,
} from '../../../../features/operatorPicking/operatorPickingRouteThunks';
import {
  routeEntered,
  routeStopSkipped,
  routeUnitAdded,
  selectRouteAccepting,
  selectRouteStop,
  selectRouteUnconfirmed,
} from '../../../../features/operatorPicking/operatorPickingRouteSlice';
import {
  ROUTE_PHASE,
  findItem,
  focusItemOf,
  openStopsOf,
  orderDone,
  orderRefOf,
  ordersText,
  parseRouteOrders,
  routeIdOf,
  routeOrdersProblem,
  routeUnitsOf,
  stopHintText,
  takeArgOf,
  takeLabel,
} from '../../../../features/operatorPicking/operatorPickingRouteUtils';
import { formatQuantity } from '../../../../utils/inventoryLabels';

/**
 * The route's orders, a chip each with how far it is; ✓ and the border, not
 * colour alone, say it is done. One still to pick says how long it has waited.
 */
const OrderChips = ({ orders }) => (
  <ul className="flex flex-wrap gap-2" aria-label="Pedidos de la ruta">
    {orders.map((order) => {
      const done = orderDone(order);
      return (
        <li
          key={order.id}
          data-testid="route-order-chip"
          data-order-id={order.id}
          data-complete={String(done)}
          className="flex flex-wrap items-center gap-1.5"
        >
          <span
            className={clsx(
              'flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-sm font-semibold text-ninja-dark dark:text-white',
              done ? 'border-ninja-green' : 'border-border'
            )}
          >
            {done && <CheckCircle2 size={14} aria-hidden="true" className="text-ninja-green" />}
            {orderRefOf(order)} · {formatQuantity(order.picked)}/{formatQuantity(order.units)}
          </span>
          {!done && <TaskTimeChip task={order} />}
        </li>
      );
    })}
  </ul>
);

/** Which orders the route is, how far it went, and those the server left out of it. */
const RouteHeader = ({ route }) => {
  const orders = route.orders || [];
  const skipped = route.skipped || [];
  const { units, picked } = routeUnitsOf(route);
  const title =
    orders.length === 0 ? 'Ruta sin pedidos' : orders.length === 1 ? orderRefOf(orders[0]) : `Ruta de ${ordersText(orders.length)}`;

  return (
    <section className="space-y-3 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
      <p className="text-lg font-bold text-ninja-dark dark:text-white">{title}</p>
      {orders.length > 0 && <OrderChips orders={orders} />}
      {units > 0 && (
        <ProgressBar
          testId="route-progress"
          value={picked}
          max={units}
          label={`${formatQuantity(picked)} de ${formatQuantity(units)} un pickeadas`}
        />
      )}
      {skipped.length > 0 && (
        <p data-testid="route-skipped" className="flex items-start gap-2 text-sm font-semibold text-ninja-dark dark:text-white">
          <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>Fuera de la ruta: {skipped.map((order) => `${orderRefOf(order)} (${order.reason})`).join(' · ')}.</span>
        </p>
      )}
    </section>
  );
};

/**
 * Picking by route, for one order or a batch of up to ten
 * (`/picking/route?orders=812,815`). Stop by stop: go to the slot and scan
 * it — or type its code, or tap it —, count the products by scanning them or
 * with «+1», and take them in one write. The server splits
 * each take among the orders; the screen shows how. «Falta» says what was
 * found, «Deshacer» on the band gives the last take back, and «Saltar parada»
 * leaves a stop for the end.
 */
export const PickingRoute = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ordersParam = searchParams.get('orders') || '';
  const orderIds = useMemo(() => parseRouteOrders(ordersParam), [ordersParam]);
  const problem = routeOrdersProblem(orderIds);
  const routeId = routeIdOf(orderIds);
  const visit = useSelector((state) => state.operatorPickingRoute);
  const stop = useSelector(selectRouteStop);
  const accepting = useSelector(selectRouteAccepting);
  const unconfirmed = useSelector(selectRouteUnconfirmed);
  const flows = useSelector(selectEnabledFlows);
  const [shortKey, setShortKey] = useState(null);

  useEffect(() => {
    const id = routeIdOf(orderIds);
    const valid = !routeOrdersProblem(orderIds);
    if (valid) dispatch(routeEntered({ routeId: id, orderIds }));
    dispatch(taskEntered({ flow: OPERATOR_FLOW.PICKING, taskId: id }));
    if (valid) dispatch(fetchPickingRoute({ orderIds }));
  }, [dispatch, orderIds]);

  const toQueue = () => navigate('/picking');

  if (problem) {
    return (
      <ErrorState title="No se puede armar esa ruta" description={problem}>
        <Button variant="ghost" onClick={toQueue}>
          Volver a la cola
        </Button>
      </ErrorState>
    );
  }
  if (visit.routeId !== routeId) return null;

  const { route, loading, error } = visit;
  if (!route && loading) {
    return (
      <div className="flex justify-center py-16" data-testid="route-loading">
        <Loader2 size={32} className="animate-spin text-ninja-green" />
      </div>
    );
  }
  if (!route && error) {
    return (
      <ErrorState
        title="No pudimos armar la ruta"
        description={error.message}
        onRetry={() => dispatch(fetchPickingRoute({ orderIds }))}
      >
        <Button variant="ghost" onClick={toQueue}>
          Volver a la cola
        </Button>
      </ErrorState>
    );
  }
  if (!route) return null;

  const { phase } = visit.stop;
  const take = takeArgOf(visit);
  const takeItem = take ? findItem(route, take.itemKey) : null;
  const focus = focusItemOf(stop, visit.stop.itemKey);
  const shortItem = shortKey ? (stop?.items || []).find((item) => item.key === shortKey) : null;
  const canPack = flows.includes(OPERATOR_FLOW.PACKING) && (route.orders || []).some(orderDone);
  const retryThunk = visit.lastWrite ? ROUTE_WRITES[visit.lastWrite.type] : takeAtStop;

  return (
    <div className="space-y-5" data-testid="pick-route" data-phase={phase}>
      <RouteHeader route={route} />

      {error && (
        <div data-testid="route-reload-error" role="alert">
          <ErrorState variant="inline" title="No pudimos recargar la ruta; puede no estar al día." description={error.message}>
            <PendingButton
              pendingOn={fetchPickingRoute}
              pendingKey={routeId}
              variant="secondary"
              size="lg"
              fullWidth
              icon={RefreshCw}
              loadingLabel="Recargando…"
              onClick={() => dispatch(fetchPickingRoute({ orderIds }))}
              data-testid="route-reload-error-retry"
            >
              Recargar la ruta
            </PendingButton>
          </ErrorState>
        </div>
      )}

      {unconfirmed && (
        <p
          data-testid="route-unconfirmed"
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-[#2a2114] dark:text-amber-300"
        >
          <AlertTriangle size={20} aria-hidden="true" className="shrink-0" />
          No sabemos si la última acción se registró. Reintenta antes de seguir: no se registra dos veces.
        </p>
      )}

      {phase === ROUTE_PHASE.DONE && (
        <>
          <RouteResult route={route} />
          <OperatorActionBar>
            {canPack && (
              <Button variant="ghost" size="lg" onClick={toQueue} data-testid="route-to-queue">
                Volver a la cola
              </Button>
            )}
            {canPack ? (
              <Button variant="primary" size="lg" onClick={() => navigate('/packing')} data-testid="route-to-packing">
                Ir a Empaque
              </Button>
            ) : (
              <Button variant="primary" size="lg" onClick={toQueue} data-testid="route-to-queue">
                Volver a la cola
              </Button>
            )}
          </OperatorActionBar>
        </>
      )}

      {phase !== ROUTE_PHASE.DONE && stop && (
        <>
          {visit.hint && (
            <p
              data-testid="route-next-hint"
              className="flex items-center gap-2 text-lg font-bold text-ninja-dark dark:text-white"
            >
              <CheckCircle2 size={22} aria-hidden="true" className="shrink-0 text-ninja-green" />
              {stopHintText(visit.hint)}
            </p>
          )}

          <RouteStopCard
            route={route}
            stop={stop}
            position={visit.stop}
            localCounts={visit.localCounts}
            locked={!accepting}
            onVerify={(code) => dispatch(scanRouteCode({ code, source: SCAN_SOURCE.TAP }))}
            onAddOne={(key) => dispatch(routeUnitAdded(key))}
            onShort={setShortKey}
          >
            <OperatorScanPanel
              prompt={
                phase === ROUTE_PHASE.SLOT
                  ? `Escanea la ubicación ${stop.slot_code}`
                  : `Escanea ${focus ? focus.sku : 'los productos'}`
              }
              hint={
                phase === ROUTE_PHASE.SLOT
                  ? 'O tócala arriba, o escribe su código abajo.'
                  : 'Cada lectura suma una: entre una unidad y otra, aparta la cámara. «+1» también suma; tocar la tarjeta, no.'
              }
              accepting={accepting}
              lastCode={visit.scan.lastCode}
              holdReads={!unconfirmed}
              busyLabel={unconfirmed ? 'Reintenta antes de seguir' : 'Registrando…'}
              onCode={(code, source, alternatives, read) =>
                dispatch(scanRouteCode({ code, source, alternatives, again: Boolean(read?.again) }))
              }
            />
          </RouteStopCard>

          <OperatorActionBar>
            <Button
              variant="ghost"
              size="lg"
              icon={SkipForward}
              disabled={!accepting || openStopsOf(route) < 2}
              onClick={() => dispatch(routeStopSkipped())}
              data-testid="route-skip-stop"
            >
              Saltar parada
            </Button>
            {unconfirmed ? (
              <PendingButton
                pendingOn={retryThunk}
                pendingKey={routeId}
                variant="primary"
                size="lg"
                icon={RotateCcw}
                loadingLabel="Reintentando…"
                onClick={() => dispatch(retryLastRouteWrite())}
                data-testid="route-retry"
              >
                Reintentar
              </PendingButton>
            ) : (
              <PendingButton
                pendingOn={takeAtStop}
                pendingKey={routeId}
                variant="primary"
                size="lg"
                disabled={!take || !accepting}
                loadingLabel="Tomando…"
                onClick={() => dispatch(submitTake())}
                data-testid="route-take-all"
              >
                {take && takeItem && takeLabel(takeItem, take.quantity)}
                {!take && (phase === ROUTE_PHASE.ITEM ? 'Cuenta lo que tomas' : `Escanea la ubicación ${stop.slot_code}`)}
              </PendingButton>
            )}
          </OperatorActionBar>
        </>
      )}

      {shortItem && (
        <RouteShortSheet
          key={shortKey}
          isOpen
          onClose={() => setShortKey(null)}
          item={shortItem}
          slotCode={stop.slot_code}
          counted={visit.localCounts[shortItem.key] || 0}
          routeId={routeId}
        />
      )}
    </div>
  );
};
